import React from 'react';
import { useFormSave } from '../hooks/useFormSave';
import {
  ContestInfoContent,
  ContestInfoItem,
  ContestInfoSection,
} from '../types';
import styles from './ContestInformation.module.css';

// DEV NOTE (backend): esta sección dejó de ser texto fijo del frontend.
// El contenido viene del template del concurso (config.contestInfo de la
// sección APPLICATION_INFORMATION — ver types/index.ts y mocks/seed.ts) y es
// administrable por la DINV por concurso. Las "fechas clave" deben derivarse
// de los atributos del Contest (openingDate, closingDate, resultsDate, etc.),
// no duplicarse como texto.

interface ContestInformationProps {
  readOnly?: boolean;
  contestInfo?: ContestInfoContent | null;
}

// Inline mínimo para el contenido configurable: **negrita** y [texto](url).
const INLINE_RE = /(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g;
const LINK_RE = /^\[([^\]]+)\]\(([^)]+)\)$/;

const renderInline = (text: string): React.ReactNode[] =>
  text.split(INLINE_RE).map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    const link = part.match(LINK_RE);
    if (link) {
      return (
        <a
          key={i}
          href={link[2]}
          target="_blank"
          rel="noopener noreferrer"
          className={`uc-a-link-inline ${styles.inlineLink}`}
        >
          {link[1]}
        </a>
      );
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });

// Lista anidada: disc → circle → square según profundidad (como el original).
const ItemList: React.FC<{ items: ContestInfoItem[]; depth?: number }> = ({
  items,
  depth = 0,
}) => (
  <ul
    className={depth === 0 ? styles.infoList : styles.infoSubList}
    style={{ listStyleType: ['disc', 'circle', 'square'][depth] || 'square' }}
  >
    {items.map((item, i) => (
      <li key={i} className="uc-p-body">
        {renderInline(item.text)}
        {item.links && item.links.length > 0 && (
          <ul
            className={styles.infoSubList}
            style={{
              listStyleType:
                ['disc', 'circle', 'square'][depth + 1] || 'square',
            }}
          >
            {item.links.map((link, j) => (
              <li key={j} className="uc-p-body">
                <a
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`uc-a-link-inline ${styles.inlineLink}`}
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        )}
        {item.children && item.children.length > 0 && (
          <ItemList items={item.children} depth={depth + 1} />
        )}
      </li>
    ))}
  </ul>
);

const Section: React.FC<{ section: ContestInfoSection }> = ({ section }) => (
  <div
    className={
      section.tone === 'highlight'
        ? styles.highlightSection
        : styles.infoSection
    }
  >
    <h3 className={`uc-h5 ${styles.subTitle}`}>{section.title}</h3>
    {section.paragraphs?.map((paragraph, i) => (
      <p key={i} className="uc-p-body">
        {renderInline(paragraph)}
      </p>
    ))}
    {section.items && <ItemList items={section.items} />}
    {section.dates && section.dates.length > 0 && (
      <div className={styles.datesList}>
        {section.dates.map((d, i) => (
          <div key={i} className={styles.dateItem}>
            <span className={`uc-h6 ${styles.dateLabel}`}>{d.label}</span>
            <span className={`uc-p-body ${styles.dateValue}`}>{d.value}</span>
          </div>
        ))}
      </div>
    )}
    {section.paragraphsAfter?.map((paragraph, i) => (
      <p key={i} className="uc-p-body">
        {renderInline(paragraph)}
      </p>
    ))}
  </div>
);

const ContestInformation: React.FC<ContestInformationProps> = ({
  contestInfo,
}) => {
  // Initialize form save hook for context awareness
  useFormSave({ stepId: 'contest-info' });

  return (
    <div className={styles.container}>
      <div className={styles.section}>
        <h2 className={`uc-h3 ${styles.sectionTitle}`}>
          Información de la convocatoria
        </h2>

        {contestInfo ? (
          <>
            <div className={styles.infoCard}>
              <h3 className={`uc-h4 ${styles.cardTitle}`}>
                {contestInfo.title}
              </h3>
              {contestInfo.intro.map((paragraph, i) => (
                <p key={i} className={`uc-p-body ${styles.cardDescription}`}>
                  {renderInline(paragraph)}
                </p>
              ))}
            </div>

            {contestInfo.sections.map((section, i) => (
              <Section key={i} section={section} />
            ))}
          </>
        ) : (
          // Fallback (p. ej. datos antiguos en localStorage sin contestInfo).
          <p className={`uc-p-body ${styles.cardDescription}`}>
            La información de esta convocatoria no está disponible. Consulte
            las bases del concurso.
          </p>
        )}

        <div className={styles.nextStepInfo}>
          <p className="uc-p-body">
            <strong>Próximo paso:</strong> Complete la información de
            antecedentes del proyecto en la siguiente sección.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ContestInformation;
